import { Box } from '@chakra-ui/react'
import {
  useActiveSpeakerId,
  useLocalSessionId,
  useParticipantIds,
  useScreenShare
} from '@daily-co/daily-react'
import React, { useMemo, useState } from 'react'
import { ParticipantTile } from '../components'
import { useActiveTelehealthSession } from '../context/active-telehealth-session.context'

const PinnedTile = ({
  width,
  participantId,
  aspectRatio,
  setAspectRatio
}: {
  width: string | object
  participantId: string
  aspectRatio: number
  setAspectRatio: (aspectRatio: number) => void
}) => {
  return (
    <Box
      position="absolute"
      top="0px"
      right="0px"
      width={width}
      height="auto"
      borderRadius="8px"
      boxShadow="lg"
      zIndex={2}
    >
      <ParticipantTile
        participantId={participantId}
        aspectRatio={aspectRatio}
        setAspectRatio={setAspectRatio}
      />
    </Box>
  )
}

const SpotlightTile = ({
  maxWidth,
  participantId,
  aspectRatio,
  setAspectRatio,
  isScreenShare = false
}: {
  maxWidth: string | object
  participantId: string
  aspectRatio: number
  setAspectRatio: (aspectRatio: number) => void
  isScreenShare?: boolean
}) => {
  return (
    <Box maxWidth={maxWidth} w={'100%'} h="auto" borderRadius="8px">
      <ParticipantTile
        participantId={participantId}
        aspectRatio={aspectRatio}
        setAspectRatio={setAspectRatio}
        isScreenShare={isScreenShare}
      />
    </Box>
  )
}

export const SpotlightLayout = () => {
  const [remoteAspectRatio, setRemoteAspectRatio] = useState<number>(16 / 9)
  const [localAspectRatio, setLocalAspectRatio] = useState<number>(16 / 9)
  const [screenShareAspectRatio, setScreenShareAspectRatio] = useState<number>(
    16 / 9
  )
  const localSessionId = useLocalSessionId()
  const participantIds = useParticipantIds({
    filter: p => p.session_id !== localSessionId
  })
  const activeParticipantId = useActiveSpeakerId({ ignoreLocal: true })

  const { isSidebarVisible } = useActiveTelehealthSession()
  const { screens } = useScreenShare()
  const screenShareParticipantId = screens[0]?.session_id || null
  const isScreenShareActive = !!screenShareParticipantId

  const isRemotePortrait = remoteAspectRatio && remoteAspectRatio < 1
  const isLocalPortrait = localAspectRatio && localAspectRatio < 1

  const localStyles = useMemo(() => {
    const baseStyles = {
      portrait: {
        width: {
          base: isLocalPortrait ? '90px' : '150px',
          sm: '200px',
          md: isSidebarVisible ? '200px' : '300px',
          lg: isSidebarVisible ? '250px' : '300px'
        }
      },
      landscape: {
        width: {
          base: isLocalPortrait ? '90px' : '150px',
          sm: '200px',
          md: '300px'
        }
      }
    }

    return isRemotePortrait ? baseStyles.portrait : baseStyles.landscape
  }, [isRemotePortrait, isSidebarVisible, isLocalPortrait])

  const maxWidth = useMemo(() => {
    const aspectRatio = isScreenShareActive
      ? screenShareAspectRatio
      : remoteAspectRatio
    const isPortrait = aspectRatio < 1

    if (isPortrait) {
      if (isLocalPortrait) {
        return `calc(100vh * ${aspectRatio} - 122px)`
      }

      return {
        base: `calc(100vh * ${aspectRatio} - 61px)`,
        sm: `calc(100vh * ${aspectRatio} - 82px)`,
        md: `calc(100vh * ${aspectRatio} - 61px)`
      }
    }

    return {
      base: `calc(100vh * ${aspectRatio} - 192px)`,
      sm: `calc(100vh * ${aspectRatio} - 257px)`,
      md: `calc(100vh * ${aspectRatio} - 192px)`
    }
  }, [
    remoteAspectRatio,
    isScreenShareActive,
    screenShareAspectRatio,
    isLocalPortrait
  ])

  const spotlightParticipantId = useMemo(() => {
    if (isScreenShareActive) {
      return screenShareParticipantId
    }

    if (participantIds.length) {
      return activeParticipantId && participantIds.includes(activeParticipantId)
        ? activeParticipantId
        : participantIds[0]
    }

    return localSessionId
  }, [
    isScreenShareActive,
    screenShareParticipantId,
    participantIds,
    activeParticipantId,
    localSessionId
  ])

  return (
    <>
      {participantIds.length > 0 && (
        <PinnedTile
          width={localStyles.width}
          participantId={
            isScreenShareActive
              ? activeParticipantId || participantIds[0]
              : localSessionId
          }
          aspectRatio={localAspectRatio}
          setAspectRatio={setLocalAspectRatio}
        />
      )}
      <SpotlightTile
        maxWidth={maxWidth}
        participantId={spotlightParticipantId}
        aspectRatio={
          isScreenShareActive ? screenShareAspectRatio : remoteAspectRatio
        }
        setAspectRatio={
          isScreenShareActive ? setScreenShareAspectRatio : setRemoteAspectRatio
        }
        isScreenShare={isScreenShareActive}
      />
    </>
  )
}
